import React, { useState, useEffect, useRef } from "react"
import { keys } from "../../keys"
import {
  CaretDown,
  Gear,
  Question,
  SignOut,
  Moon,
  Sun,
  List,
} from "@phosphor-icons/react"
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar"
import { useAuthStore, useUIStore } from "../../stores"
import { DivisionSelector } from "./DivisionSelector"
import { PageBreadcrumb } from "./PageBreadcrumb"
import { PinnedPagesDropdown } from "./PinnedPagesDropdown"
import { cn } from "../../lib/utils"
import { getLogoutUrl } from "../../lib/auth"
import { SystemTicketDrawer } from "../../features/system-tickets"

export function Header() {
  const { user } = useAuthStore()
  const { setMobileMenuOpen, theme, setTheme, domains } = useUIStore()

  // Get app2 dashboard URL for account links
  const appUrl = domains?.APP || keys.APP_URL

  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [ticketDrawerOpen, setTicketDrawerOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)

  // Close menu on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleLogout = () => {
    setUserMenuOpen(false)
    window.location.href = getLogoutUrl()
  }

  const handleSettingsClick = () => {
    setUserMenuOpen(false)
    window.open(`${appUrl}/settings`, "_blank")
  }

  const handleHelpClick = () => {
    setUserMenuOpen(false)
    setTicketDrawerOpen(true)
  }

  const initials = user
    ? `${user.given_name?.[0] || ""}${user.family_name?.[0] || ""}`
    : "U"

  const displayName = user
    ? `${user.given_name || ""} ${user.family_name || ""}`.trim()
    : "User"

  const isOnline = true // TODO: Wire to actual online status

  return (
    <div
      className={cn(
        "relative flex flex-col z-50",
        "bg-background/95 backdrop-blur-sm",
        "border-b border-border/80",
        "shadow-[0_1px_3px_0_rgba(0,0,0,0.02)]"
      )}
    >
      {/* Single header row */}
      <header className="flex h-14 items-center justify-between px-4 gap-2 lg:gap-4">
        {/* Left side: hamburger + breadcrumb */}
        <div className="flex items-center gap-3">
          {/* Mobile/Tablet: Hamburger menu */}
          <button
            className="flex items-center justify-center h-10 w-10 -ml-2 lg:hidden rounded-lg hover:bg-accent transition-colors"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            <List className="h-5 w-5 text-muted-foreground" />
          </button>

          {/* Page breadcrumb */}
          <PageBreadcrumb />
        </div>

        {/* Right side: pinned, division, notifications, user */}
        <div className="flex items-center gap-1 lg:gap-2">
          {/* Pinned pages */}
          <PinnedPagesDropdown />

          {/* Divider */}
          <div className="hidden lg:block h-6 w-px bg-border mx-1" />

          {/* Division selector */}
          <DivisionSelector />

          {/* User menu */}
          <div ref={userMenuRef} className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className={cn(
                "flex items-center gap-2.5 rounded-lg px-2 py-1.5",
                "transition-all duration-200",
                userMenuOpen ? "bg-accent shadow-inner" : "hover:bg-muted"
              )}
            >
              {/* Avatar with online status ring */}
              <div className="relative">
                <Avatar
                  className={cn(
                    "h-8 w-8 ring-2 ring-offset-1 transition-all duration-200",
                    isOnline ? "ring-success" : "ring-border"
                  )}
                >
                  <AvatarImage src={user?.avatar} />
                  <AvatarFallback className="text-sm bg-primary text-white font-semibold">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                {/* Status indicator */}
                <span
                  className={cn(
                    "absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-background",
                    isOnline ? "bg-success shadow-sm shadow-success/50" : "bg-muted-foreground"
                  )}
                />
              </div>
              <span className="hidden text-sm font-medium text-foreground lg:inline">
                {displayName}
              </span>
              <CaretDown
                className={cn(
                  "h-3 w-3 text-muted-foreground hidden lg:block transition-transform duration-200",
                  userMenuOpen && "rotate-180"
                )}
              />
            </button>

            {/* User dropdown menu */}
            {userMenuOpen && (
              <div
                className={cn(
                  "absolute right-0 top-full mt-2 w-64 rounded-xl",
                  "bg-background border border-border shadow-xl",
                  "overflow-hidden z-50",
                  "animate-flyout-enter"
                )}
              >
                {/* Header section */}
                <div className="px-4 py-3 bg-gradient-to-br from-muted to-background border-b border-border">
                  <p className="text-sm font-semibold text-foreground">{displayName}</p>
                  <p className="text-xs text-muted-foreground">{user?.email || "user@example.com"}</p>
                  <div
                    className={cn(
                      "flex items-center gap-1.5 mt-2 text-xs",
                      isOnline ? "text-success" : "text-muted-foreground"
                    )}
                  >
                    <span
                      className={cn(
                        "h-2 w-2 rounded-full",
                        isOnline ? "bg-success" : "bg-muted-foreground"
                      )}
                    />
                    <span>{isOnline ? "Online" : "Offline"}</span>
                  </div>
                </div>

                {/* Menu items */}
                <div className="py-1">
                  <button
                    onClick={handleSettingsClick}
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                  >
                    <Gear className="h-4 w-4 text-muted-foreground" />
                    <span>Settings</span>
                    <span className="ml-auto text-xs text-muted-foreground font-mono bg-muted px-1.5 py-0.5 rounded">
                      ,
                    </span>
                  </button>
                  <button
                    onClick={() => {
                      const newTheme = theme === "light" ? "dark" : "light"
                      setTheme(newTheme)
                    }}
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                  >
                    {theme === "light" ? (
                      <Moon className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Sun className="h-4 w-4 text-yellow-400" />
                    )}
                    <span>Theme</span>
                    <span className="ml-auto text-xs text-muted-foreground capitalize">{theme}</span>
                  </button>
                </div>

                <div className="border-t border-border py-1">
                  <button
                    onClick={handleHelpClick}
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                  >
                    <Question className="h-4 w-4 text-muted-foreground" />
                    <span>Help & Support</span>
                  </button>
                </div>

                <div className="border-t border-border py-1">
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-danger hover:bg-danger/5 transition-colors"
                  >
                    <SignOut className="h-4 w-4" />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <SystemTicketDrawer open={ticketDrawerOpen} onOpenChange={setTicketDrawerOpen} />
    </div>
  )
}
